/**
 * Browser telemetry, correlated with the API's (M0-29).
 *
 * The point is not page views. It is that a request the user made and the
 * server work it caused end up on ONE trace: the SDK attaches a W3C
 * `traceparent` to outgoing calls, and the API's OpenTelemetry instrumentation
 * continues that trace instead of starting a new one. Without it you have two
 * unrelated halves of every incident and no way to join them.
 *
 * The SPA and the API are on different origins — Static Web Apps and Container
 * Apps — so the correlation headers are cross-origin, which is why
 * `enableCorsCorrelation` and `correlationHeaderDomains` are both required. The
 * API already allows any request header via CORS, so nothing is needed there.
 */
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { redactTelemetryUrls } from './telemetryRedaction';

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ?? '';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

let appInsights: ApplicationInsights | undefined;

/** The API's host, so correlation headers go there and nowhere else. */
function apiHost(): string | undefined {
  if (!apiBaseUrl) return undefined;
  try {
    return new URL(apiBaseUrl).hostname;
  } catch {
    return undefined;
  }
}

/**
 * Starts telemetry when a connection string was built in, and does nothing when
 * it was not — which is the case for local development and every test run.
 * Configured by presence rather than by mode so that a developer *can* point a
 * local build at a real resource when they need to reproduce something.
 */
export function initTelemetry(): ApplicationInsights | undefined {
  if (!connectionString || appInsights) return appInsights;

  const host = apiHost();

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Cross-origin correlation to the API, and only to the API. Listing the
      // host explicitly keeps trace headers off third-party requests, which
      // would leak our trace ids to whoever we call.
      enableCorsCorrelation: true,
      correlationHeaderDomains: host ? [host] : [],
      distributedTracingMode: 2, // W3C traceparent, matching the API
      enableAutoRouteTracking: true,
      disableCookiesUsage: true,
      autoTrackPageVisitTime: true,
    },
  });

  // BEFORE the first page view, and before any request can be instrumented:
  // an item sent ahead of this registration is one nothing redacts. Keeping a
  // search term out of the URL (M1-33) covers the page view and NOT the
  // dependency the SDK records for the API call itself — see
  // `telemetryRedaction.ts` (CC-17).
  appInsights.addTelemetryInitializer(redactTelemetryUrls);

  appInsights.loadAppInsights();
  appInsights.trackPageView();
  return appInsights;
}

/**
 * Reports something that broke. Unhandled errors are collected automatically;
 * this is for the ones the app catches and handles, which would otherwise be
 * invisible precisely because they were handled well.
 */
export function trackError(error: Error, properties?: Record<string, unknown>): void {
  appInsights?.trackException({ exception: error, properties });
}
